import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { DEVICE_OFFLINE_THRESHOLD_SEC, HEARTBEAT_SEC } from '@pbtv/shared';
import { PageHeader } from '../components/PageHeader.js';
import { EmptyState } from '../components/EmptyState.js';
import { apiBaseUrl } from '../lib/api.js';
import { useSession } from '../lib/session.js';

interface SectionCard {
  to: string;
  title: string;
  /** The milestone that fills the section in; absent once it is live. */
  milestone?: string;
  summary: string;
}

const SECTIONS: readonly SectionCard[] = [
  {
    to: '/stores',
    title: 'Stores',
    summary: 'Brand, timezone and opening hours for each site. Everything schedules off these.',
  },
  {
    to: '/screens',
    title: 'Screens',
    summary: 'The fleet, its health, and what each TV is called, sized and turned to.',
  },
  {
    to: '/media',
    title: 'Media',
    milestone: 'M2',
    summary: 'Videos and images, per store or shared across brands, with expiry dates.',
  },
  {
    to: '/playlists',
    title: 'Playlists',
    milestone: 'M2',
    summary: 'Order, image durations, video repeats, mute and fit for every item.',
  },
  {
    to: '/devices',
    title: 'Devices',
    milestone: 'M4',
    summary: 'Which TVs are alive, what they are playing, and the remote commands to fix them.',
  },
  {
    to: '/audit',
    title: 'Audit log',
    milestone: 'M4',
    summary: 'Every admin action and every sign-in, with the before-and-after of each change.',
  },
];

export function OverviewPage(): ReactElement {
  const { user } = useSession();

  return (
    <>
      <PageHeader
        title={user === null ? 'Overview' : `Kia ora, ${firstName(user.name) || user.email}`}
        description={
          user === null
            ? 'Fleet overview.'
            : user.role === 'admin'
              ? 'You have admin access: every store, every screen, and the release channel.'
              : scopeDescription(user.storeIds)
        }
      />

      <div className="card-grid">
        {SECTIONS.map((section) => (
          <Link key={section.to} to={section.to} className="section-card">
            <span className="section-card__head">
              <span className="section-card__title">{section.title}</span>
              {section.milestone !== undefined ? (
                <span className="badge badge--milestone">{section.milestone}</span>
              ) : null}
            </span>
            <span className="section-card__summary">{section.summary}</span>
          </Link>
        ))}
      </div>

      <EmptyState
        milestone="M4"
        title="Fleet health lands here"
        description={`Once devices are reporting, this page leads with the screens that need attention rather than a list of links. Devices heartbeat every ${HEARTBEAT_SEC} seconds and are called offline after ${Math.round(DEVICE_OFFLINE_THRESHOLD_SEC / 60)} minutes of silence, weighed against the store's own opening hours so a TV that is off overnight is not an incident.`}
        points={[
          'Screens offline now, with how long and whether the store is even open',
          'Content errors: downloads that failed, hashes that did not match, content sets too big for the device',
          'Devices that lost their ADB grants and will not come back after the next reboot',
          'Clock skew and app versions across the fleet, including any update waiting on a remote press',
        ]}
      />

      <dl className="facts">
        <div>
          <dt>API</dt>
          <dd className="mono">{describeApiBase()}</dd>
        </div>
      </dl>
    </>
  );
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? '';
}

function scopeDescription(storeIds: readonly string[]): string {
  if (storeIds.length === 0) {
    return 'No stores are assigned to your account yet. An admin needs to add you to one.';
  }
  if (storeIds.length === 1) return 'You manage one store.';
  return `You manage ${storeIds.length} stores.`;
}

function describeApiBase(): string {
  try {
    return apiBaseUrl();
  } catch {
    return 'VITE_API_URL is not set in this build';
  }
}
